import { Button, Modal } from 'antd'
import { createGlobalStyle, styled } from 'styled-components'
import type { WindrawStatusTone } from './status'

export const FieldGridLayout = createGlobalStyle`
  .ordersForm .ordersForm__field-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr)) minmax(290px, 330px);
    gap: 26px;
    justify-content: stretch;
  }

  @media (max-width: 1799px) {
    .ordersForm .ordersForm__field-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(280px, 310px);
    }
  }

  @media (max-width: 1399px) {
    .ordersForm .ordersForm__field-grid {
      grid-template-columns: minmax(0, 1fr) minmax(280px, 330px);
    }
  }

  @media (max-width: 1099px) {
    .ordersForm .ordersForm__field-grid {
      display: block;
    }
  }
`

export const Panel = styled.section`
  min-width: 0;
  align-self: start;
  border-left: 1px solid #e1e6ef;
  background: ${({ theme }) => theme.colors.white};
  color: #253449;

  .ordersForm .ordersForm__field-grid & {
    grid-column: 4;
    grid-row: 1 / span 3;
  }

  @media (max-width: 1799px) {
    .ordersForm .ordersForm__field-grid & {
      grid-column: 3;
    }
  }

  @media (max-width: 1399px) {
    .ordersForm .ordersForm__field-grid & {
      grid-column: 2;
      grid-row: 1 / span 4;
    }
  }

  @media (max-width: 1099px) {
    .ordersForm .ordersForm__field-grid & {
      margin-top: 18px;
    }
  }
`

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 39px;
  padding: 7px 16px 7px 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};

  h3 {
    margin: 0;
    color: inherit;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.4;
  }
`

export const RefreshButton = styled(Button)`
  color: inherit;

  &&:hover,
  &&:focus-visible {
    color: inherit;
    background: rgb(255 255 255 / 16%);
  }
`

export const PanelBody = styled.div`
  padding: 0 18px 13px;
`

export const OrderSummary = styled.div`
  padding-top: 14px;

  & + & {
    margin-top: 18px;
    border-top: 1px solid #e5e9ef;
  }
`

const statusPalette: Record<WindrawStatusTone, { background: string; color: string }> = {
  neutral: { background: '#f1f2f5', color: '#5d6573' },
  error: { background: '#fff0ee', color: '#a83d38' },
  planning: { background: '#f0eff8', color: '#625b91' },
  production: { background: '#eaf2ff', color: '#285f9e' },
  warehouse: { background: '#fff4e5', color: '#8c570e' },
  shipped: { background: '#e9f5ed', color: '#28764d' },
}

export const Status = styled.span<{ $tone: WindrawStatusTone }>`
  display: block;
  width: fit-content;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 3px;
  background: ${({ $tone }) => statusPalette[$tone].background};
  color: ${({ $tone }) => statusPalette[$tone].color};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
`

export const Facts = styled.div`
  display: flex;
  flex-direction: column;

  & > :first-child {
    padding-top: 0;
  }
`

export const Fact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
`

export const FactLabel = styled.span`
  color: #5f5f5f;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
`

export const FactValue = styled.div`
  overflow-wrap: anywhere;
  color: #262626;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
`

export const EventMarkTrack = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 4px;
`

export const EventMarkSegment = styled.span<{
  $connected: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  cursor: help;

  &::before {
    position: absolute;
    top: 10px;
    left: calc(-50% - 4px);
    width: calc(100% + 4px);
    height: 2px;
    background: ${({ $connected }) => ($connected ? '#3b9469' : '#dfe5ed')};
    content: '';
  }

  &:first-child::before {
    display: none;
  }

  > span:last-child {
    color: #6b7688;
    font-size: 10px;
    line-height: 1.3;
    text-align: center;
  }

  &:focus-visible {
    outline: none;

    > span:first-child {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 2px;
    }
  }
`

export const EventMarkNode = styled.span<{
  $state: 'missing' | 'unmarked' | 'partial' | 'marked'
}>`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border: ${({ $state }) => {
    if ($state === 'missing') return '2px dashed #cbd2dc'
    if ($state === 'unmarked') return '2px solid #cbd2dc'
    return '0'
  }};
  border-radius: 50%;
  background: ${({ $state }) => {
    if ($state === 'marked') return '#3b9469'
    if ($state === 'partial') return '#bd8748'
    return '#fff'
  }};
  color: #fff;
  font-size: 12px;
`

export const MoreButton = styled(Button)`
  width: 100%;
  margin-top: 14px;
`

export const Message = styled.p<{ $error?: boolean }>`
  margin: 0;
  padding: 16px 0 3px;
  color: ${({ $error }) => ($error ? '#ac4a4a' : '#637187')};
  font-size: 13px;
  line-height: 1.5;
`

export const Loading = styled.div`
  padding-top: 20px;
`

export const Freshness = styled.div`
  margin-top: 14px;
  padding-top: 11px;
  border-top: 1px solid #e5e9ef;
  color: #7b8799;
  font-size: 11px;
  line-height: 1.4;
`

export const DetailsModal = styled(Modal)`
  .ant-modal-body {
    max-height: min(72vh, 720px);
    overflow-y: auto;
  }
`

export const DetailsOverview = styled.dl`
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e6eaf0;

  ${Status} {
    margin: 0;
  }
`

export const DetailsOverviewItem = styled.div`
  display: grid;
  grid-template-columns: minmax(130px, 170px) minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  min-height: 39px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 3px;
  }
`

export const DetailsOverviewLabel = styled.dt`
  color: #5f5f5f;
  font-size: 13px;
  font-weight: 600;
`

export const DetailsOverviewValue = styled.dd`
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  color: #262626;
  font-size: 13px;
`

export const DetailsNote = styled.p`
  margin: 2px 0 12px;
  color: #738096;
  font-size: 12px;
  line-height: 1.5;
`

export const StageMark = styled.span<{ $muted?: boolean }>`
  color: ${({ $muted }) => ($muted ? 'rgba(0, 0, 0, 0.45)' : 'inherit')};
  font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
`

export const TableScroll = styled.div`
  overflow-x: auto;
`

export const DetailsTable = styled.table`
  width: 100%;
  min-width: 650px;
  border-collapse: collapse;
  font-size: 13px;
  text-align: left;

  th {
    padding: 10px 14px 10px 0;
    border-bottom: 1px solid #dfe5ee;
    font-weight: 600;
    white-space: nowrap;
  }

  td {
    padding: 11px 14px 11px 0;
    border-bottom: 1px solid #edf0f4;
    vertical-align: top;
  }

  td:last-child,
  th:last-child {
    padding-right: 0;
  }
`

export const PositionName = styled.span`
  display: block;
`

export const PositionMeta = styled.span`
  display: block;
  margin-top: 3px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
`

export const Empty = styled.p`
  color: #738096;
  font-size: 12px;
`
